import React, { useState, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { AgentPanel, getAgentDisplayName } from '../../../redux/features/streaming/streamingSlice';
import { AgentPanelData } from '../../../libs/models/ChatMessage';

interface AgentPanelItemProps {
    panel: AgentPanel | AgentPanelData;
    defaultExpanded?: boolean;
}

const PREVIEW_LINES = 3;

const AgentPanelItem: React.FC<AgentPanelItemProps> = ({ panel, defaultExpanded = false }) => {
    const [elapsedSec, setElapsedSec] = useState(0);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        if (panel.status !== 'running') { return; }
        const interval = setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - panel.startTime) / 1000));
        }, 1000);
        return () => { clearInterval(interval); };
    }, [panel.status, panel.startTime]);

    const elapsed = panel.endTime
        ? ((panel.endTime - panel.startTime) / 1000).toFixed(1)
        : elapsedSec > 0 ? `${elapsedSec}` : null;

    const statusIcon = panel.status === 'running' ? '●' : panel.status === 'completed' ? '✓' : '✗';
    const statusColor = panel.status === 'running' ? '#0078d4' : panel.status === 'completed' ? '#107c10' : '#d13438';

    const [expanded, setExpanded] = useState(defaultExpanded);
    const hasContent = panel.content.length > 0;

    const lines = useMemo(() => panel.content.split('\n'), [panel.content]);
    const hasMoreLines = lines.length > PREVIEW_LINES;
    const displayContent = useMemo(() => {
        if (showAll || !hasMoreLines) return panel.content;
        return lines.slice(-PREVIEW_LINES).join('\n');
    }, [panel.content, lines, hasMoreLines, showAll]);

    return (
        <div style={{ marginBottom: '4px' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: '#666',
                    padding: '2px 0',
                    cursor: hasContent ? 'pointer' : 'default',
                }}
                onClick={hasContent ? () => { setExpanded((v) => !v); } : undefined}
            >
                <span style={{ color: statusColor, fontSize: '8px' }}>{statusIcon}</span>
                <span style={{ fontWeight: 500, color: '#444' }}>{getAgentDisplayName(panel.agentName)}</span>
                {elapsed && <span style={{ color: '#999', fontSize: '11px' }}>({elapsed}s)</span>}
                {panel.status === 'running' && (
                    <span style={{ display: 'inline-flex', gap: '2px', marginLeft: '2px' }}>
                        <span className="thinking-dot" style={{ animationDelay: '0s' }} />
                        <span className="thinking-dot" style={{ animationDelay: '0.2s' }} />
                        <span className="thinking-dot" style={{ animationDelay: '0.4s' }} />
                    </span>
                )}
                {hasContent && (
                    <span style={{ color: '#ccc', fontSize: '10px', marginLeft: 'auto' }}>
                        {expanded ? '▲' : '▼'}
                    </span>
                )}
            </div>
            {hasContent && expanded && (
                <div style={{ marginTop: '2px', marginLeft: '14px' }}>
                    <div
                        style={{
                            padding: '6px 8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            borderLeft: '2px solid #0078d4',
                            fontSize: '11px',
                            color: '#555',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: showAll ? '300px' : undefined,
                            overflowY: showAll ? 'auto' : undefined,
                            lineHeight: '1.5',
                        }}
                    >
                        {displayContent}
                        {panel.status === 'running' && (
                            <span style={{
                                display: 'inline-block',
                                width: '6px',
                                height: '11px',
                                backgroundColor: '#0078d4',
                                marginLeft: '1px',
                                verticalAlign: 'text-bottom',
                                animation: 'cursorBlink 1s step-end infinite',
                            }} />
                        )}
                    </div>
                    {hasMoreLines && (
                        <div
                            onClick={(e) => { e.stopPropagation(); setShowAll((v) => !v); }}
                            style={{
                                fontSize: '11px',
                                color: '#0078d4',
                                cursor: 'pointer',
                                padding: '3px 8px 0',
                                userSelect: 'none',
                            }}
                        >
                            {showAll ? '▲ Gizle' : '▼ Tümünü göster'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface ThinkingIndicatorProps {
    chatId?: string;
    panels?: AgentPanelData[];
    defaultCollapsed?: boolean;
}

export const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ chatId, panels: staticPanels, defaultCollapsed = false }) => {
    const streaming = useAppSelector((state: RootState) => state.streaming);
    const streamState = chatId ? streaming[chatId] as typeof streaming[string] | undefined : undefined;

    // Use Redux panels during streaming, static panels for persisted messages
    const activePanels = streamState?.agentPanels ?? staticPanels;
    const isStreaming = streamState?.isStreaming ?? false;

    const [isOpen, setIsOpen] = useState(!defaultCollapsed);

    if (!activePanels || activePanels.length === 0) {
        return null;
    }

    const hasContent = activePanels.some((p) => p.content.length > 0 || p.status === 'running');
    if (!isStreaming && !hasContent) {
        return null;
    }

    return (
        <div style={{ marginBottom: '4px' }}>
            <style>{`
                @keyframes thinkingBounce {
                    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                    40% { transform: translateY(-4px); opacity: 1; }
                }
                @keyframes cursorBlink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0; }
                }
                .thinking-dot {
                    display: inline-block;
                    width: 4px;
                    height: 4px;
                    border-radius: 50%;
                    background-color: #0078d4;
                    animation: thinkingBounce 1.2s ease-in-out infinite;
                }
            `}</style>
            <div
                onClick={() => { setIsOpen(!isOpen); }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#888',
                    userSelect: 'none',
                    padding: '2px 0',
                }}
            >
                <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span>Agent Akış Süreci</span>
                <span style={{ color: '#bbb', fontSize: '11px' }}>({activePanels.length})</span>
            </div>
            {isOpen && (
                <div style={{ paddingLeft: '14px', marginTop: '2px' }}>
                    {activePanels.map((panel) => (
                        <AgentPanelItem key={panel.agentName} panel={panel} defaultExpanded={false} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ThinkingIndicator;
